export type ParsedVersion = {
  nativeVersion: string;
  buildNumber: number;
  jsCodeVersion: string;
};

type Response = {
  status: "latest" | "outdated" | "critical" | "withIssues";
  currentUserVersion: string | null;
  /**
   * The installed native binary is behind — only a store update can deliver it.
   * `true` as well when either version string is unparseable, so callers keep pointing
   * at the store rather than promising an in-app update they cannot verify.
   */
  nativeOutdated: boolean;
  /** The JS bundle is behind. On its own (`nativeOutdated === false`) this is an OTA update. */
  jsOutdated: boolean;
};

// Function to parse the version string and extract the native version, build number, and js code version
export function parseVersion(version: string): ParsedVersion | null {
  const regex = /(\d+\.\d+\.\d+)(?: \((\d+)\))? - (\d+\.\d+\.\d+)(?: \((\w+)\))?/;
  const match = version.match(regex);
  if (!match) {
    return null;
  }
  return {
    nativeVersion: match[1] || "",
    buildNumber: parseInt(match[2] || "", 10) || 0,
    jsCodeVersion: match[3] || "",
  };
}

export function compareDottedVersions(v1: string, v2: string): number {
  const parts1 = v1.split(".").map(Number);
  const parts2 = v2.split(".").map(Number);
  const length = Math.max(parts1.length, parts2.length);

  for (let i = 0; i < length; i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;
    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }
  return 0;
}

export function compareClientAppVersion({
  currentUserVersion,
  requiredVersion,
  currentAvailableVersion,
  versionsWithIssues = [],
}: {
  currentUserVersion: string | null;
  requiredVersion: string;
  currentAvailableVersion: string;
  versionsWithIssues?: string[];
}): Response {
  if (!currentUserVersion) {
    return { status: "latest", currentUserVersion, nativeOutdated: false, jsOutdated: false };
  }

  const userVersion = parseVersion(currentUserVersion);
  const latestVersion = parseVersion(currentAvailableVersion);

  // The version string carries two independent halves — the native build and the JS bundle —
  // and each has its own remedy: a newer binary comes from the store, a newer bundle is an OTA
  // the app applies itself. Reporting them separately lets callers pick the right one instead
  // of sending everyone to a store listing that holds the build they already run (TRE-1885).
  const nativeOutdated =
    !userVersion || !latestVersion || compareDottedVersions(userVersion.nativeVersion, latestVersion.nativeVersion) < 0;
  const jsOutdated =
    !!userVersion &&
    !!latestVersion &&
    compareDottedVersions(userVersion.jsCodeVersion, latestVersion.jsCodeVersion) < 0;

  if (versionsWithIssues?.length) {
    const currentWithoutMobile = currentUserVersion.replace(" (mobile)", "");
    if (versionsWithIssues.includes(currentWithoutMobile)) {
      return { status: "withIssues", currentUserVersion, nativeOutdated, jsOutdated };
    }
  }

  if (!userVersion || !latestVersion) {
    return { status: "outdated", currentUserVersion, nativeOutdated, jsOutdated };
  }

  if (!nativeOutdated && !jsOutdated) {
    return { status: "latest", currentUserVersion, nativeOutdated, jsOutdated };
  }

  const requiredVer = parseVersion(requiredVersion);

  if (!requiredVer) {
    return { status: "outdated", currentUserVersion, nativeOutdated, jsOutdated };
  }

  const nativeComparisonRequired = compareDottedVersions(userVersion.nativeVersion, requiredVer.nativeVersion);
  const jsComparisonRequired = compareDottedVersions(userVersion.jsCodeVersion, requiredVer.jsCodeVersion);

  if (nativeComparisonRequired < 0 || jsComparisonRequired < 0) {
    return { status: "critical", currentUserVersion, nativeOutdated, jsOutdated };
  }

  return { status: "outdated", currentUserVersion, nativeOutdated, jsOutdated };
}
